<template>
  <div class="su-wrap">
    <div class="su-head">
      <div>
        <h2 class="su-title">Supported Users</h2>
        <p class="su-sub">Create helper accounts and grant only the actions you choose. They act within your data and can never do more than you can.</p>
      </div>
      <div class="su-head-actions">
        <button class="su-btn" @click="openAudit">Activity Log</button>
        <button class="su-btn su-btn-primary" @click="openCreate">+ Add Supported User</button>
      </div>
    </div>

    <!-- List -->
    <div v-if="loading" class="su-empty">Loading…</div>
    <div v-else-if="!users.length" class="su-empty">No supported users yet. Click “Add Supported User” to create one.</div>
    <template v-else>
      <!-- Bulk toolbar: select all + group activate/deactivate -->
      <div class="su-toolbar">
        <label class="su-selectall">
          <input type="checkbox" :checked="allSelected" @change="toggleSelectAll($event)" />
          <span>{{ selectedIds.size ? (selectedIds.size + ' selected') : 'Select all' }}</span>
        </label>
        <div class="su-toolbar-actions" v-if="selectedIds.size">
          <button class="su-btn su-btn-primary" :disabled="bulkBusy" @click="bulkSetStatus(1)">Activate selected</button>
          <button class="su-btn su-btn-warn" :disabled="bulkBusy" @click="bulkSetStatus(0)">Deactivate selected</button>
        </div>
      </div>

      <div class="su-list">
        <div
          v-for="u in users"
          :key="u.id"
          class="su-card"
          :class="{ 'is-inactive': !u.status, 'is-selected': selectedIds.has(u.id) }"
        >
          <input
            type="checkbox"
            class="su-card-check"
            :checked="selectedIds.has(u.id)"
            @change="toggleSelect(u.id)"
          />
          <div class="su-card-main">
            <div class="su-card-name">
              {{ u.name || u.username }}
              <span :class="['su-badge', u.status ? 'su-badge-on' : 'su-badge-off']">{{ u.status ? 'Active' : 'Inactive' }}</span>
            </div>
            <div class="su-card-user">@{{ u.username }}</div>

            <!-- Compact overview instead of every permission -->
            <div class="su-card-over">
              <span class="su-over-total">{{ u.permTotal || 0 }} permission{{ (u.permTotal || 0) === 1 ? '' : 's' }}</span>
              <span v-if="u.scopes && u.scopes.length" class="su-over-dim">· {{ u.scopes.length }} scope{{ u.scopes.length === 1 ? '' : 's' }}</span>
              <span v-if="u.policies && u.policies.length" class="su-over-dim">· {{ u.policies.length }} polic{{ u.policies.length === 1 ? 'y' : 'ies' }}</span>
            </div>
            <div v-if="(u.scopes && u.scopes.length) || (u.policies && u.policies.length)" class="su-card-scopes">
              <span v-for="s in u.scopes" :key="s.module" class="su-chip su-chip-scope">{{ s.module }}<b>{{ s.count }}</b></span>
              <span v-for="p in u.policies" :key="'p'+p.id" class="su-chip su-chip-policy">{{ p.name }}</span>
            </div>
            <div v-else class="su-card-none">No permissions granted</div>
          </div>

          <div class="su-card-actions">
            <button class="su-btn" @click="openEdit(u)">Edit</button>
            <button v-if="u.status" class="su-btn su-btn-warn" :disabled="bulkBusy" @click="setStatus(u, 0)">Deactivate</button>
            <button v-else class="su-btn su-btn-primary" :disabled="bulkBusy" @click="setStatus(u, 1)">Activate</button>
          </div>
        </div>
      </div>
    </template>

    <!-- Create/Edit modal -->
    <div v-if="showForm" class="su-modal-backdrop" @click.self="closeForm">
      <div class="su-modal">
        <div class="su-modal-head">
          <h3>{{ editingId ? 'Edit Supported User' : 'Add Supported User' }}</h3>
          <button class="su-x" @click="closeForm">×</button>
        </div>

        <div class="su-modal-body">
          <div class="su-grid">
            <div class="bf-field">
              <label>Name</label>
              <input v-model.trim="form.name" class="bf-input" placeholder="Full name" />
            </div>
            <div class="bf-field">
              <label>Username</label>
              <input v-model.trim="form.username" class="bf-input" :disabled="!!editingId" placeholder="login username" />
            </div>
            <div class="bf-field">
              <label>{{ editingId ? 'Reset password (optional)' : 'Password' }}</label>
              <input v-model.trim="form.password" type="password" class="bf-input" placeholder="••••••" />
            </div>
            <div class="bf-field" v-if="editingId">
              <label>Status</label>
              <select v-model="form.status" class="bf-input">
                <option :value="1">Active</option>
                <option :value="0">Inactive</option>
              </select>
            </div>
          </div>

          <!-- Template presets -->
          <div class="su-templates">
            <span class="su-tpl-label">Start from a template:</span>
            <button v-for="t in templates" :key="t.name" type="button" class="su-tpl-btn" @click="applyTemplate(t)">{{ t.name }}</button>
            <button type="button" class="su-tpl-btn su-tpl-clear" @click="clearPerms">Clear all</button>
          </div>

          <!-- Permission picker grouped by module -->
          <div class="su-perms">
            <div v-for="(perms, mod) in groupedPerms" :key="mod" class="su-perm-group">
              <div class="su-perm-group-head">
                <span class="su-perm-mod">{{ mod }}</span>
                <button type="button" class="su-mini" @click="toggleModule(mod, true)">all</button>
                <button type="button" class="su-mini" @click="toggleModule(mod, false)">none</button>
              </div>
              <label v-for="p in perms" :key="p.perm_key" class="su-perm-item" :class="{ 'is-action': p.is_action }">
                <input type="checkbox" :checked="selected.has(p.perm_key)" @change="togglePerm(p.perm_key)" />
                <span class="su-perm-text">
                  <span class="su-perm-label">
                    {{ p.label }}
                    <span v-if="p.sensitive" class="su-sensitive" title="Sensitive — exposes secrets, resets credentials, or moves money. Grant deliberately.">Sensitive</span>
                  </span>
                  <span class="su-perm-desc">{{ p.description }}</span>
                </span>
              </label>
            </div>
          </div>

          <!-- Capabilities (per-user flags, G1) -->
          <div v-if="availableCapabilities.length" class="su-caps">
            <div class="su-caps-head">Capabilities</div>
            <p class="su-caps-hint">Extra abilities this user may use. You can only grant abilities you hold yourself, and the action still needs its matching permission selected above — otherwise it stays view-only.</p>
            <label v-for="c in availableCapabilities" :key="c.key" class="su-cap-item">
              <input type="checkbox" v-model="form.flags[c.key]" />
              <span>{{ c.label }}</span>
            </label>
          </div>

          <!-- Save as reusable policy -->
          <label class="su-saveas">
            <input type="checkbox" v-model="form.saveAsPolicy" />
            <span>Save this set as a reusable policy</span>
            <input v-if="form.saveAsPolicy" v-model.trim="form.saveAsPolicyName" class="bf-input su-saveas-name" placeholder="Policy name" />
          </label>

          <p v-if="formError" class="bf-error">{{ formError }}</p>
        </div>

        <div class="su-modal-foot">
          <span class="su-count">{{ selected.size }} permission{{ selected.size === 1 ? '' : 's' }} selected</span>
          <div>
            <button class="su-btn" @click="closeForm">Cancel</button>
            <button class="su-btn su-btn-primary" :disabled="saving" @click="submit">{{ saving ? 'Saving…' : (editingId ? 'Save changes' : 'Create') }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Activity Log modal -->
    <div v-if="showAudit" class="su-modal-backdrop" @click.self="showAudit = false">
      <div class="su-modal su-modal-wide">
        <div class="su-modal-head">
          <h3>Activity Log</h3>
          <button class="su-x" @click="showAudit = false">×</button>
        </div>
        <div class="su-modal-body">
          <div class="su-audit-filters">
            <input v-model.trim="auditFilters.actor" class="bf-input" placeholder="Username" @keyup.enter="loadAudit(1)" />
            <select v-model="auditFilters.outcome" class="bf-input" @change="loadAudit(1)">
              <option value="">All outcomes</option>
              <option value="success">Success</option>
              <option value="deny">Denied (403)</option>
              <option value="error">Error</option>
            </select>
            <input type="date" v-model="auditFilters.startDate" class="bf-input" @change="loadAudit(1)" />
            <input type="date" v-model="auditFilters.endDate" class="bf-input" @change="loadAudit(1)" />
            <button class="su-btn" @click="loadAudit(1)">Refresh</button>
          </div>

          <div v-if="auditLoading" class="su-empty">Loading…</div>
          <div v-else-if="!auditRows.length" class="su-empty">No activity yet.</div>
          <div v-else class="su-audit-table-wrap">
            <table class="su-audit-table">
              <thead>
                <tr><th>When</th><th>User</th><th>Event</th><th>Permission</th><th>Action</th><th>Outcome</th><th>ms</th></tr>
              </thead>
              <tbody>
                <tr v-for="r in auditRows" :key="r.id">
                  <td class="su-audit-time">{{ r.created_at }}</td>
                  <td>{{ r.actor_username }}</td>
                  <td>{{ r.event_type }}</td>
                  <td><code>{{ r.permission || '—' }}</code></td>
                  <td class="su-audit-ep"><span class="su-audit-m">{{ r.method }}</span> {{ r.endpoint }}</td>
                  <td><span :class="['su-out', 'su-out-' + (r.outcome || 'na')]">{{ r.outcome || '—' }}</span></td>
                  <td>{{ r.processing_ms != null ? r.processing_ms : '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="su-modal-foot">
          <span class="su-count">{{ auditTotal }} event{{ auditTotal === 1 ? '' : 's' }}</span>
          <div>
            <button class="su-btn" :disabled="auditPage <= 1 || auditLoading" @click="loadAudit(auditPage - 1)">Prev</button>
            <button class="su-btn" :disabled="auditPage * auditLimit >= auditTotal || auditLoading" @click="loadAudit(auditPage + 1)">Next</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from "../../http-common";

export default {
  name: "supported-users",
  data() {
    return {
      loading: true,
      users: [],
      permissions: [],   // ceiling-filtered catalog
      templates: [],
      showForm: false,
      editingId: null,
      selected: new Set(),
      selectedIds: new Set(),  // bulk row selection
      bulkBusy: false,
      // Activity log viewer
      showAudit: false,
      auditRows: [],
      auditLoading: false,
      auditTotal: 0,
      auditPage: 1,
      auditLimit: 50,
      auditFilters: { actor: "", outcome: "", startDate: "", endDate: "" },
      saving: false,
      formError: "",
      // Capability flags (G1). Each maps to a users-table column; the parent
      // can only enable one it holds itself (checked via the store getter).
      // The backend re-clamps to the parent on write and live-caps at the
      // money path, so this is UI affordance only.
      capabilityDefs: [
        { key: "can_approve_payin_manually", label: "Approve pay-ins manually", parentGetter: "canApprovePayinManually" },
        { key: "can_partial_approve_payout", label: "Partially approve pay-outs", parentGetter: "canPartialApprovePayout" },
        { key: "can_pickup_partial_payout", label: "Pick up partially-settled pay-outs", parentGetter: "canPickupPartialPayout" },
        { key: "can_manage_clients", label: "Manage clients", parentGetter: "canManageClients" },
        { key: "payOut", label: "Create pay-outs", parentGetter: "payOutStatus" },
        { key: "payIn", label: "Create pay-ins", parentGetter: "payInStatus" },
      ],
      form: { name: "", username: "", password: "", status: 1, saveAsPolicy: false, saveAsPolicyName: "", flags: {} },
    };
  },
  computed: {
    groupedPerms() {
      const g = {};
      for (const p of this.permissions) {
        (g[p.module] = g[p.module] || []).push(p);
      }
      return g;
    },
    allSelected() {
      return this.users.length > 0 && this.selectedIds.size === this.users.length;
    },
    // Only the capabilities the PARENT (the logged-in creator) actually holds —
    // you can't delegate a flag you don't have.
    availableCapabilities() {
      return this.capabilityDefs.filter((c) => this.parentHasFlag(c.parentGetter));
    },
  },
  async mounted() {
    await Promise.all([this.loadUsers(), this.loadCatalog()]);
  },
  methods: {
    async loadUsers() {
      this.loading = true;
      try {
        const r = await http.get("/supported-users");
        this.users = r.data.data || [];
        // Drop any selections that no longer exist in the refreshed list.
        const live = new Set(this.users.map((u) => u.id));
        this.selectedIds = new Set([...this.selectedIds].filter((id) => live.has(id)));
      } catch (e) { /* ignore */ }
      this.loading = false;
    },
    toggleSelect(id) {
      if (this.selectedIds.has(id)) this.selectedIds.delete(id);
      else this.selectedIds.add(id);
      this.selectedIds = new Set(this.selectedIds); // reactivity
    },
    toggleSelectAll(e) {
      this.selectedIds = e.target.checked ? new Set(this.users.map((u) => u.id)) : new Set();
    },
    async setStatus(u, status) {
      await this.applyStatus([u.id], status);
    },
    async bulkSetStatus(status) {
      await this.applyStatus([...this.selectedIds], status);
    },
    async applyStatus(ids, status) {
      if (!ids.length || this.bulkBusy) return;
      this.bulkBusy = true;
      try {
        await http.post("/supported-users/bulk-status", { ids, status });
        this.selectedIds = new Set();
        await this.loadUsers();
      } catch (e) { /* ignore */ }
      this.bulkBusy = false;
    },
    async loadCatalog() {
      try {
        const r = await http.get("/supported-users/catalog");
        this.permissions = r.data.permissions || [];
        this.templates = r.data.templates || [];
      } catch (e) { /* ignore */ }
    },
    // True if the PARENT (logged-in creator) holds this flag — drives which
    // capability toggles are offered. payIn/payOut come back as "1"/"0".
    parentHasFlag(getterName) {
      const v = this.$store.getters[getterName];
      return v === true || v === 1 || v === "1";
    },
    emptyFlags() {
      const f = {};
      for (const c of this.capabilityDefs) f[c.key] = false;
      return f;
    },
    openAudit() {
      this.showAudit = true;
      this.loadAudit(1);
    },
    async loadAudit(page) {
      this.auditPage = Math.max(1, page || 1);
      this.auditLoading = true;
      try {
        const f = this.auditFilters;
        const params = { page: this.auditPage, limit: this.auditLimit };
        if (f.actor) params.actor = f.actor;
        if (f.outcome) params.outcome = f.outcome;
        if (f.startDate) params.startDate = f.startDate;
        if (f.endDate) params.endDate = f.endDate;
        const r = await http.get("/supported-users/audit", { params });
        this.auditRows = r.data.data || [];
        this.auditTotal = r.data.total || 0;
      } catch (e) {
        this.auditRows = [];
        this.auditTotal = 0;
      }
      this.auditLoading = false;
    },
    openCreate() {
      this.editingId = null;
      this.selected = new Set();
      this.form = { name: "", username: "", password: "", status: 1, saveAsPolicy: false, saveAsPolicyName: "", flags: this.emptyFlags() };
      this.formError = "";
      this.showForm = true;
    },
    openEdit(u) {
      this.editingId = u.id;
      // Seed capability toggles from the supporting user's own flag columns
      // (listUsers returns them).
      const flags = this.emptyFlags();
      for (const c of this.capabilityDefs) flags[c.key] = Number(u[c.key]) === 1;
      this.form = { name: u.name, username: u.username, password: "", status: u.status, saveAsPolicy: false, saveAsPolicyName: "", flags };
      // Seed selected from direct allow-overrides (policies shown as chips;
      // editing re-sets the direct grant — policies can be re-added later).
      this.selected = new Set((u.overrides || []).filter(o => o.effect === "allow").map(o => o.perm_key));
      this.formError = "";
      this.showForm = true;
    },
    closeForm() { this.showForm = false; },
    togglePerm(k) {
      if (this.selected.has(k)) this.selected.delete(k); else this.selected.add(k);
      this.selected = new Set(this.selected); // reactivity
    },
    toggleModule(mod, on) {
      for (const p of (this.groupedPerms[mod] || [])) {
        if (on) this.selected.add(p.perm_key); else this.selected.delete(p.perm_key);
      }
      this.selected = new Set(this.selected);
    },
    applyTemplate(t) {
      this.selected = new Set(t.perms);
    },
    clearPerms() { this.selected = new Set(); },
    async submit() {
      this.formError = "";
      if (!this.editingId && (!this.form.username || !this.form.password)) {
        this.formError = "Username and password are required."; return;
      }
      if (this.form.saveAsPolicy && !this.form.saveAsPolicyName) {
        this.formError = "Give the reusable policy a name."; return;
      }
      this.saving = true;
      // Only send capability flags the parent actually holds (defensive — the
      // backend re-clamps regardless).
      const flags = {};
      for (const c of this.availableCapabilities) flags[c.key] = !!(this.form.flags && this.form.flags[c.key]);
      const payload = {
        name: this.form.name,
        permissions: Array.from(this.selected),
        status: this.form.status,
        saveAsPolicy: this.form.saveAsPolicy,
        saveAsPolicyName: this.form.saveAsPolicyName,
        flags,
      };
      if (this.form.password) payload.password = this.form.password;
      try {
        if (this.editingId) {
          await http.post(`/supported-users/${this.editingId}`, payload);
        } else {
          payload.username = this.form.username;
          await http.post("/supported-users", payload);
        }
        this.showForm = false;
        await this.loadUsers();
      } catch (e) {
        this.formError = (e.response && e.response.data && e.response.data.message) || "Save failed.";
      }
      this.saving = false;
    },
  },
};
</script>

<style scoped>
.su-wrap { padding: 20px 22px; }
.su-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; margin-bottom: 22px; padding-bottom: 18px; border-bottom: 1px solid #eef0f5; }
.su-title { font-size: 20px; font-weight: 700; color: #1b2a47; margin: 0; }
.su-sub { color: #6b7280; font-size: 13px; margin: 6px 0 0; max-width: 560px; line-height: 1.5; }
.su-btn { border: 1px solid #d7dbe6; background: #fff; color: #1b2a47; border-radius: 10px; padding: 9px 16px; font-weight: 600; cursor: pointer; font-size: 13px; transition: all .15s; }
.su-btn:hover { border-color: #b8bfd0; }
.su-btn-primary { background: #00296f; border-color: #00296f; color: #fff; }
.su-btn-primary:hover { background: #013a99; }
.su-head-actions { display: flex; gap: 8px; flex-shrink: 0; }
.su-btn-warn { color: #b45309; border-color: #f3d9a8; }
/* Activity log */
.su-modal-wide { max-width: 1000px; }
.su-audit-filters { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 14px; }
.su-audit-filters .bf-input { width: auto; min-width: 140px; }
.su-audit-table-wrap { overflow-x: auto; }
.su-audit-table { width: 100%; border-collapse: collapse; font-size: 12px; }
.su-audit-table th { text-align: left; color: #8a92a6; font-weight: 700; font-size: 10px; text-transform: uppercase; letter-spacing: .04em; padding: 6px 10px; border-bottom: 1px solid #eef0f5; white-space: nowrap; }
.su-audit-table td { padding: 7px 10px; border-bottom: 1px solid #f4f6fa; color: #1b2a47; vertical-align: top; }
.su-audit-time { white-space: nowrap; color: #6b7280; font-variant-numeric: tabular-nums; }
.su-audit-table code { font-size: 11px; color: #00296f; }
.su-audit-ep { color: #465066; word-break: break-all; }
.su-audit-m { font-weight: 700; color: #8a92a6; }
.su-out { font-size: 10px; font-weight: 700; padding: 1px 7px; border-radius: 999px; text-transform: uppercase; }
.su-out-success { background: #e6f6ec; color: #1a8b48; }
.su-out-deny { background: #fdeaec; color: #cb3a47; }
.su-out-error { background: #fdf3e3; color: #b45309; }
.su-out-na { background: #f1f3f7; color: #8a92a6; }
.su-btn-warn:hover { background: #fdf6ea; }
.su-btn:disabled { opacity: .55; cursor: default; }
.su-empty { padding: 40px; text-align: center; color: #8a92a6; background: #f8f9fc; border-radius: 12px; }
/* Modern, on-theme checkbox for the whole block (list rows, select-all,
   permission pickers, save-as). Replaces the default browser control with a
   navy square + white tick, matching the app theme (#00296f). */
.su-wrap input[type="checkbox"] {
  appearance: none;
  -webkit-appearance: none;
  box-sizing: border-box;
  width: 18px;
  height: 18px;
  margin: 0;
  border: 1.5px solid #cbd2e0;
  border-radius: 5px;
  background: #fff;
  position: relative;
  flex-shrink: 0;
  cursor: pointer;
  transition: background .15s ease, border-color .15s ease;
}
.su-wrap input[type="checkbox"]:hover { border-color: #00296f; }
.su-wrap input[type="checkbox"]:checked { background: #00296f; border-color: #00296f; }
.su-wrap input[type="checkbox"]:checked::after {
  content: "";
  position: absolute;
  left: 6px;
  top: 2px;
  width: 5px;
  height: 10px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.su-wrap input[type="checkbox"]:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(0, 41, 111, .18);
}
.su-wrap input[type="checkbox"]:disabled { opacity: .5; cursor: default; }
/* Bulk toolbar */
.su-toolbar { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 12px; padding: 4px 2px; min-height: 34px; }
.su-selectall { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #465066; cursor: pointer; user-select: none; font-weight: 600; }
.su-selectall input { cursor: pointer; }
.su-toolbar-actions { display: flex; gap: 8px; }
.su-list { display: flex; flex-direction: column; gap: 10px; }
.su-card { display: flex; align-items: flex-start; gap: 14px; background: #fff; border: 1px solid #eef0f5; border-radius: 12px; padding: 16px 18px; transition: border-color .15s, background .15s; }
.su-card.is-selected { border-color: #00296f; background: #fafbff; }
.su-card.is-inactive { background: #fcfcfd; }
.su-card.is-inactive .su-card-name { color: #8a92a6; }
/* `top` nudge (general rule is position:relative) aligns the box with the
   name line without fighting the shared checkbox styling. */
.su-card-check { top: 3px; }
.su-card-main { flex: 1; min-width: 0; }
.su-card-name { font-weight: 700; color: #1b2a47; display: flex; align-items: center; gap: 8px; }
.su-card-user { color: #8a92a6; font-size: 12px; margin: 2px 0 8px; }
.su-card-over { font-size: 12px; color: #465066; margin-bottom: 8px; }
.su-over-total { font-weight: 700; color: #00296f; }
.su-over-dim { color: #8a92a6; margin-left: 4px; }
.su-card-scopes { display: flex; flex-wrap: wrap; gap: 6px; }
.su-chip { background: #f2f4f8; color: #465066; border-radius: 999px; padding: 3px 10px; font-size: 11px; }
.su-chip-scope { background: #f2f4f8; color: #465066; text-transform: capitalize; }
.su-chip-scope b { color: #00296f; margin-left: 5px; }
.su-chip-policy { background: #e7edff; color: #00296f; font-weight: 600; }
.su-card-none { color: #b0b6c3; font-size: 12px; }
.su-badge { font-size: 10px; padding: 2px 8px; border-radius: 999px; font-weight: 700; }
.su-badge-on { background: #e6f6ec; color: #1a8b48; }
.su-badge-off { background: #f1f3f7; color: #8a92a6; }
.su-card-actions { display: flex; gap: 8px; flex-shrink: 0; align-items: center; }
/* modal */
.su-modal-backdrop { position: fixed; inset: 0; background: rgba(16,22,41,.5); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; }
.su-modal { background: #fff; border-radius: 16px; width: 100%; max-width: 760px; max-height: 90vh; display: flex; flex-direction: column; overflow: hidden; }
.su-modal-head { display: flex; justify-content: space-between; align-items: center; padding: 18px 22px; border-bottom: 1px solid #eef0f5; }
.su-modal-head h3 { margin: 0; font-size: 17px; color: #1b2a47; }
.su-x { border: none; background: none; font-size: 26px; line-height: 1; color: #9aa1b2; cursor: pointer; }
.su-modal-body { padding: 20px 22px; overflow-y: auto; }
.su-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 18px; }
/* Explicit input styling so the Name/Username/Password fields are always
   visible inside the modal regardless of global CSS scoping. */
.su-modal .bf-field { display: flex; flex-direction: column; gap: 5px; }
.su-modal .bf-field label { font-size: 12px; font-weight: 600; color: #465066; }
.su-modal .bf-input {
  display: block; width: 100%; box-sizing: border-box;
  height: 40px; padding: 8px 12px;
  border: 1px solid #d7dbe6; border-radius: 9px;
  background: #fff; color: #1b2a47; font-size: 14px;
  outline: none;
}
.su-modal .bf-input:focus { border-color: #00296f; box-shadow: 0 0 0 2px rgba(0,41,111,.12); }
.su-modal .bf-input:disabled { background: #f4f6fa; color: #8a92a6; }
.su-modal select.bf-input { appearance: auto; }
.su-templates { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; padding: 12px 0; border-top: 1px dashed #eef0f5; }
.su-tpl-label { font-size: 12px; color: #6b7280; font-weight: 600; }
.su-tpl-btn { border: 1px solid #d7dbe6; background: #f8f9fc; border-radius: 8px; padding: 5px 12px; font-size: 12px; cursor: pointer; }
.su-tpl-btn:hover { background: #eef1f8; }
.su-tpl-clear { color: #cb3a47; }
.su-perms { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 8px; }
.su-perm-group { border: 1px solid #eef0f5; border-radius: 10px; padding: 12px; }
.su-perm-group-head { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.su-perm-mod { font-weight: 700; font-size: 12px; text-transform: uppercase; color: #00296f; letter-spacing: .04em; flex: 1; }
.su-mini { border: none; background: #eef1f8; border-radius: 6px; padding: 2px 8px; font-size: 11px; cursor: pointer; color: #465066; }
.su-perm-item { display: flex; align-items: flex-start; gap: 8px; padding: 6px 2px; font-size: 13px; cursor: pointer; border-radius: 6px; }
.su-perm-item:hover { background: #f8f9fc; }
.su-perm-item input { top: 2px; }
.su-perm-text { display: flex; flex-direction: column; gap: 1px; }
.su-perm-label { font-weight: 600; color: #1b2a47; line-height: 1.25; }
.su-sensitive {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 7px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
  color: #b45309;
  background: #fdf3e3;
  border: 1px solid #f3d9a8;
  border-radius: 999px;
  vertical-align: middle;
}
.su-perm-desc { font-size: 11px; color: #7a8296; line-height: 1.3; }
.su-caps { margin-top: 18px; padding-top: 14px; border-top: 1px dashed #eef0f5; }
.su-caps-head { font-weight: 700; font-size: 12px; text-transform: uppercase; letter-spacing: .04em; color: #00296f; }
.su-caps-hint { font-size: 11px; color: #7a8296; margin: 4px 0 10px; line-height: 1.4; }
.su-cap-item { display: flex; align-items: center; gap: 8px; padding: 5px 2px; font-size: 13px; color: #1b2a47; cursor: pointer; }
.su-saveas { display: flex; align-items: center; gap: 10px; margin-top: 18px; padding-top: 14px; border-top: 1px dashed #eef0f5; font-size: 13px; }
.su-saveas-name { max-width: 220px; }
.su-modal-foot { display: flex; justify-content: space-between; align-items: center; padding: 16px 22px; border-top: 1px solid #eef0f5; }
.su-count { font-size: 12px; color: #6b7280; }
.su-modal-foot > div { display: flex; gap: 10px; }
@media (max-width: 640px) { .su-grid, .su-perms { grid-template-columns: 1fr; } }
</style>
